import { Link } from "react-router-dom";
import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { useMaterials, useApplications } from "@/hooks/useBioNexusData";
import { Plus, FileText, AlertTriangle, CheckCircle } from "lucide-react";

const AdminReview = () => {
  const { data: materials } = useMaterials();
  const { data: applications } = useApplications();

  // Find materials without proper sources
  const materialsWithoutSource = materials?.filter(m => !m.source_doi && !m.source_paper) || [];
  
  // Find materials with data but no source tracking
  const materialsNeedingReview = materials?.filter(m => {
    const hasData = m.degradation_days || m.tensile_strength_mpa || m.cost_per_kg;
    const hasSource = m.source_doi || m.source_paper;
    return hasData && !hasSource;
  }) || [];

  return (
    <div className="min-h-screen bg-background page-enter">
      <Header />
      
      <main className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <div className="mb-8">
            <BackNav to="/" label="Back to Home" />
          </div>

          <h1 className="font-display font-medium text-4xl mb-2">Admin Dashboard</h1>
          <p className="font-mono text-xs uppercase text-muted-foreground mb-12">
            Content management & academic integrity
          </p>

          {/* Quick Actions */}
          <section className="mb-12">
            <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Quick Actions
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/admin/review"
                className="inline-flex items-center gap-2 px-6 py-3 border border-primary text-primary font-mono text-sm hover:bg-primary/10 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Review Queue
              </Link>
              <Link 
                to="/admin/content"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border font-mono text-sm hover:bg-muted transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Content Management
              </Link>
              <Link 
                to="/admin/materials/new"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-mono text-sm hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Add New Material
              </Link>
            </div>
          </section>

          {/* Data Integrity Warnings */}
          {materialsNeedingReview.length > 0 && (
            <section className="mb-12 border border-amber-500/30 bg-amber-500/5 p-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-6 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                需要审核 - Materials Need Source Verification
              </h2>
              <div className="space-y-4">
                {materialsNeedingReview.map(m => (
                  <div key={m.id} className="flex items-center justify-between border-b border-border pb-4">
                    <div>
                      <p className="font-display font-medium">{m.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">
                        Has data but no paper source - needs verification
                      </p>
                    </div>
                    <Link 
                      to={`/materials/${m.id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      Review →
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Materials Overview */}
          <section className="mb-12">
            <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Materials Overview
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border border-border p-6">
                <p className="font-mono text-xs text-muted-foreground uppercase mb-2">Total Materials</p>
                <p className="font-display font-medium text-4xl">{materials?.length || 0}</p>
              </div>
              <div className="border border-primary p-6">
                <p className="font-mono text-xs text-muted-foreground uppercase mb-2">With Paper Source</p>
                <p className="font-display font-medium text-4xl text-primary">
                  {materials?.filter(m => m.source_doi || m.source_paper).length || 0}
                </p>
              </div>
              <div className="border border-amber-500/50 p-6">
                <p className="font-mono text-xs text-muted-foreground uppercase mb-2">Missing Sources</p>
                <p className="font-display font-medium text-4xl text-amber-400">
                  {materialsWithoutSource.length}
                </p>
              </div>
            </div>
          </section>

          {/* All Materials Table */}
          <section>
            <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              All Materials
            </h2>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="font-mono text-xs uppercase text-muted-foreground py-4">Name</th>
                  <th className="font-mono text-xs uppercase text-muted-foreground py-4">Family</th>
                  <th className="font-mono text-xs uppercase text-muted-foreground py-4">Source</th>
                  <th className="font-mono text-xs uppercase text-muted-foreground py-4">Data</th>
                  <th className="font-mono text-xs uppercase text-muted-foreground py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {materials?.map((material) => {
                  const hasSource = material.source_doi || material.source_paper;
                  const dataCount = [
                    material.degradation_value || material.degradation_days,
                    material.tensile_strength_mpa,
                    material.electrical_performance,
                    material.water_resistance,
                    material.cost_per_kg
                  ].filter(Boolean).length;

                  return (
                    <tr key={material.id} className="border-b border-border">
                      <td className="py-4">
                        <span className="font-display font-medium">{material.name}</span>
                        {material.name_cn && (
                          <span className="text-muted-foreground ml-2 text-sm">{material.name_cn}</span>
                        )}
                      </td>
                      <td className="py-4">
                        <span className="font-mono text-xs">{material.family || '—'}</span>
                      </td>
                      <td className="py-4">
                        {hasSource ? (
                          <span className="inline-flex items-center gap-1 text-primary text-xs">
                            <FileText className="w-3 h-3" />
                            {material.source_doi || 'Has source'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            No source
                          </span>
                        )}
                      </td>
                      <td className="py-4">
                        {dataCount >= 3 ? (
                          <span className="inline-flex items-center gap-1 text-primary text-xs">
                            <CheckCircle className="w-3 h-3" />
                            {dataCount}/5
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                            {dataCount}/5
                          </span>
                        )}
                      </td>
                      <td className="py-4 text-right">
                        <Link 
                          to={`/materials/${material.id}`}
                          className="font-mono text-xs text-primary hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminReview;